import type { ApiNode } from '@goalbuddy/client';
import { useMutation } from '@tanstack/react-query';
import { Check, SkipForward, Trash2, Zap } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { DestinationPicker } from './destination-picker';
import { Badge, Button, Card, ProgressBar } from './ui';
import { TypeIcon } from './node-meta';

/** File inbox items one by one: pick a destination, mark as next action, finish, drop or skip. */
export function InboxTriage({ items, onExit }: { items: ApiNode[]; onExit: () => void }) {
  const [total] = useState(items.length);
  const [skipped, setSkipped] = useState<number[]>([]);
  const queue = items.filter((n) => !skipped.includes(n.id));
  const node = queue[0];
  const move = useMutation({
    mutationFn: (v: { id: number; parent: number | null }) => api.moveNode(v.id, v.parent),
  });
  const update = useMutation({
    mutationFn: (v: { id: number; next: boolean }) => api.updateNode(v.id, { is_next_action: v.next }),
  });
  const done = useMutation({ mutationFn: (id: number) => api.doneNode(id) });
  const drop = useMutation({ mutationFn: (id: number) => api.dropNode(id) });
  const [next, setNext] = useState(false);

  if (!node) {
    return (
      <Card className="flex flex-col items-center gap-3 p-8 text-center">
        <div className="text-3xl">🎉</div>
        <div className="text-lg font-semibold">
          {skipped.length ? `${skipped.length} left for later` : 'Inbox zero'}
        </div>
        <Button onClick={onExit}>Back to inbox</Button>
      </Card>
    );
  }
  const handled = total - queue.length;
  return (
    <Card className="space-y-4 p-5">
      <div className="flex items-center gap-3">
        <ProgressBar value={handled / Math.max(total, 1)} className="flex-1" />
        <span className="text-xs text-muted-fg">
          {handled} of {total}
        </span>
        <Button size="sm" variant="ghost" onClick={onExit}>
          Exit
        </Button>
      </div>
      <div key={node.id} className="animate-rise space-y-1">
        <div className="flex items-center gap-2 text-xs text-muted-fg">
          <TypeIcon type={node.type} size={13} /> {node.type}
          {node.context && <Badge>{node.context}</Badge>}
        </div>
        <h2 className="text-xl font-semibold leading-snug">{node.title}</h2>
        {node.description_short && <p className="text-sm text-muted-fg">{node.description_short}</p>}
      </div>
      <div className="space-y-2">
        <div className="text-xs font-medium text-muted-fg">File it under</div>
        <DestinationPicker
          key={node.id}
          node={node}
          onPick={async (parent) => {
            if (next && node.type === 'task') await update.mutateAsync({ id: node.id, next: true });
            if (parent === null) {
              toast('Left in inbox');
              setSkipped((s) => [...s, node.id]);
              return;
            }
            move.mutate({ id: node.id, parent }, { onSuccess: () => toast.success('Filed') });
          }}
        />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
        {node.type === 'task' ? (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="accent-[var(--color-primary)]"
              checked={next}
              onChange={(e) => setNext(e.target.checked)}
            />
            <Zap size={13} className="text-primary" /> Mark as next action when filing
          </label>
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setSkipped((s) => [...s, node.id])}>
            <SkipForward size={13} /> Skip
          </Button>
          {node.type === 'task' && (
            <Button size="sm" variant="outline" onClick={() => done.mutate(node.id)}>
              <Check size={13} /> Done
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => drop.mutate(node.id)}>
            <Trash2 size={13} /> Drop
          </Button>
        </div>
      </div>
    </Card>
  );
}
